import React from 'react'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBBtnGroup,
  MDBCollapse,
} from 'mdb-react-ui-kit'
import { addWeeks, startOfWeek, format, getQuarter, getISOWeek } from 'date-fns'
import { UIDatePickerInput, UIInput } from './../../../../components/forms/form-fields'
import DateManager from '../../../../shared/utilities/DateManager.class'
import ToggleAvAca from './ToggleAvAca.component'
import ProductionWidget from './ProductionWidget.component'
import ComparativeDateCalculator from './ComparativeDateCalculator.class'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import UsertypeService from '../../../../shared/services/Usertype.service'
import { UserFactory } from '../../../../shared/factories'
import ExportLeaderGrowthReportModal from './ExportLeaderGrowthReportModal'

import './LeaderWidget.scss'

const ENABLE_PER_DAY = false
let timerInt = null

class AgentSelectorStore {
  searchTerm = ''
  selectedDownline = ''
  isOpen = false
  agents = []

  constructor() {
    makeAutoObservable(this)
  }

  onSearchKeyDown = evt => {
    if (evt.key === 'Enter' && this.searchTerm.length > 2) this.performSearch()
  }

  performSearch = async () => {
    let agents = []
    if (this.searchTerm) {
      if (UserProfileService.isA(['system-admin', 'internal-admin', 'internal-staff'])) {
        agents =
          (await UserFactory.search({
            search: { _global: this.searchTerm },
            pagination: false,
          })) || []
      } else {
        agents =
          (await UserFactory.search({
            search: {
              _global: this.searchTerm,
              downline_of: UserProfileService.getUserId(),
            },
            pagination: false,
          })) || []
      }
    }

    this.agents = agents
      .filter(
        agent =>
          [38, 90, 93, 130].includes(agent.get('usertype_id')) && agent.get('id') !== UserProfileService.getUserId()
      )
      .sort((a, b) =>
        `${a.get('u_fname')} ${a.get('u_lname')}`.localeCompare(`${b.get('u_fname')} ${b.get('u_lname')}`, 'en')
      )

    this.isOpen = this.agents.length > 0

    return Promise.resolve(this.agents)
  }
}

const Store = new AgentSelectorStore()

const LeaderSelectorComponent = ({ onAgentSelected }) => {
  const isAuth = UserProfileService.isA([
    'system-admin',
    'internal-admin',
    'internal-staff',
    'district-leader',
    'regional-leader',
    'divisional-leader',
    'premier-agent',
  ])

  if (!isAuth) return null

  return (
    <MDBRow id='GrowthWidgetLeaderSelectorComponent' style={{ backgroundColor: '#FFF', borderRadius: '6px' }}>
      <MDBCol size='12' md='10' style={{ position: 'relative' }}>
        <UIInput
          type='text'
          label='Search Downline Leaders'
          value={Store.searchTerm}
          onKeyDown={evt => Store.onSearchKeyDown(evt)}
          onChange={e => {
            const v = e.target.value
            Store.searchTerm = v

            if (timerInt) {
              window.clearTimeout(timerInt)
              timerInt = null
            }

            timerInt = setTimeout(() => {
              Store.performSearch()

              if (timerInt) {
                window.clearTimeout(timerInt)
                timerInt = null
              }
            }, 1000)
          }}
        />
      </MDBCol>
      <MDBCol size='12' md='2' style={{ position: 'relative', display: 'flex', justifyContent: 'end' }}>
        <MDBBtn
          type='button'
          size='sm'
          disabled={Store.searchTerm === ''}
          onClick={() => {
            Store.searchTerm = ''
            Store.selectedDownline = null
            Store.agents = []
            Store.isOpen = false
            onAgentSelected(false)
          }}
          color='secondary'
          style={{ display: 'flex' }}
        >
          <span>
            <MDBIcon icon='times' />
          </span>
          &nbsp;<span>Reset</span>
        </MDBBtn>
      </MDBCol>
      <div className='dropdown-wrapper'>
        <div className='dropdown-inner'>
          <MDBCollapse open={Store.isOpen}>
            <ul>
              {Store.agents.map((agent, idx) => {
                return (
                  <li
                    key={`agent-selectable--${agent.id()}-${idx}`}
                    onClick={() => {
                      Store.searchTerm = [agent.get('u_fname'), agent.get('u_lname')].join(' ').trim()
                      Store.isOpen = false
                      onAgentSelected(agent)
                    }}
                  >
                    <strong>{agent.get('u_fname') + ' ' + agent.get('u_lname') + ' '}</strong>
                    <br />
                    <small>{agent.get('u_email')} </small>|
                    <small> {UsertypeService.idToName(agent.get('usertype_id'))}</small>
                  </li>
                )
              })}
            </ul>
          </MDBCollapse>
        </div>
      </div>
    </MDBRow>
  )
}

const LeaderSelector = observer(LeaderSelectorComponent)

class LeaderWidget extends ProductionWidget {
  Calc = null

  state = {
    reportDate: new Date(),
    metric: 'points',
    data: {},
    prevYear: new Date().getFullYear() - 1,
    diffPrevYear: 1,
    loading: true,
    fetching: true,
    error: null,
    isLoading: false,
    isModalOpen: false,
    exportData: {
      columns: [
        {
          label: 'Agent',
          field: 'id',
          sort: 'asc',
        },
        {
          label: 'Usertype',
          field: 'usertype',
          sort: 'asc',
        },
        {
          label: 'Report Timespan',
          field: 'timespan',
        },
        {
          label: 'Current Value',
          field: 'current',
        },
        {
          label: 'Previous Value',
          field: 'previous',
        },
        {
          label: 'Growth Value',
          field: 'growth',
        },
      ],
      rows: [],
    },
    exportReportDate: new Date(),
    exportPrevYear: new Date().getFullYear() - 1,
    exportTimespanInd: 'WTD',
    currentData: null,
    previousData: null,
    exportmetric: 'points',
  }

  async componentDidMount() {
    this._searchMethod = 'searchLeader'
    this._enableUrlSync = true
    this.Calc = new ComparativeDateCalculator()

    const { userId } = this._initFromQueryParams()

    // Populate AgentSelectorStore with agent name from URL user_id
    if (userId && !isNaN(userId) && parseInt(userId) > 0) {
      try {
        const agent = await UserFactory.findById(parseInt(userId))
        if (agent) {
          Store.searchTerm = [agent.get('u_fname'), agent.get('u_lname')].join(' ').trim()
          Store.selectedDownline = agent
          Store.isOpen = false
        }
      } catch (ex) {}
    }

    this._fetch()
    this._updateQueryString()
  }

  render() {
    const { fetching } = this.state

    return (
      <MDBContainer id='LeaderWidgetComponent' fluid className={ENABLE_PER_DAY ? 'show-per-day-forecast' : ''}>
        <MDBRow>
          <MDBCol size='12'>
            <ul className='source-buttons'>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'points'}
                  onClick={() => {
                    this.setState({ metric: 'points' }, () => {
                      this._setMetric('points')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Points
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || ['av', 'av_no_aca'].includes(this.state.metric)}
                  onClick={() => {
                    this.setState({ metric: 'av' }, () => {
                      this._setMetric('av')
                      this._updateQueryString()
                    })
                  }}
                  color={!['av', 'av_no_aca'].includes(this.state.metric) ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View AV
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'aca_lives'}
                  onClick={() => {
                    this.setState({ metric: 'aca_lives' }, () => {
                      this._setMetric('aca_lives')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'aca_lives' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View ACA Lives
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'group'}
                  onClick={() => {
                    this.setState({ metric: 'group' }, () => {
                      this._setMetric('group')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'group' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Groups
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'enrolled'}
                  onClick={() => {
                    this.setState({ metric: 'enrolled' }, () => {
                      this._setMetric('enrolled')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'enrolled' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Recruiting
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'ma_lives'}
                  onClick={() => {
                    this.setState({ metric: 'ma_lives' }, () => {
                      this._setMetric('ma_lives')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'ma_lives' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Med Advantange
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'life_av'}
                  onClick={() => {
                    this.setState({ metric: 'life_av' }, () => {
                      this._setMetric('life_av')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'life_av' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Life
                </MDBBtn>
              </li>
              <li>
                <MDBBtn
                  type='button'
                  disabled={fetching || this.state.metric === 'med_supps_av'}
                  onClick={() => {
                    this.setState({ metric: 'med_supps_av' }, () => {
                      this._setMetric('med_supps_av')
                      this._updateQueryString()
                    })
                  }}
                  color={this.state.metric !== 'med_supps_av' ? 'indigo' : 'blue-grey'}
                >
                  <MDBIcon icon='check' />
                  &nbsp;View Med Supps
                </MDBBtn>
              </li>
            </ul>
          </MDBCol>
          <MDBCol size='12' className='mt-4'>
            <LeaderSelector
              onAgentSelected={agent => {
                if (agent) {
                  this._searchId = agent.id()
                  this._fetchByDownline()
                  this._updateQueryString()
                } else if (agent === false) {
                  this._searchId = ''
                  this._fetch()
                  this._updateQueryString()
                }
              }}
            />
          </MDBCol>
          <MDBCol size='12'>
            <ToggleAvAca
              isOpen={['av', 'av_no_aca'].includes(this.state.metric)}
              isLoading={fetching}
              value={this.state.metric === 'av_no_aca'}
              onChange={removeAcaAv =>
                this.setState({ metric: removeAcaAv ? 'av_no_aca' : 'av' }, () => this._updateQueryString())
              }
            />
          </MDBCol>
        </MDBRow>
        <hr />
        <MDBRow>
          <MDBCol size='12'>
            <MDBContainer fluid>
              <MDBRow className='widget-date-selector'>
                <MDBCol size='12' sm='12' md='2' className='wds-input'>
                  <UIDatePickerInput
                    label='Report Date'
                    name='report_date'
                    id='dpw_report_date'
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    dateFormat='MM/dd/yyyy'
                    maxDate={new Date()}
                    selected={this.state.reportDate}
                    onChange={date => this._onDateChange(date?.target?.value)}
                  />
                </MDBCol>
                <MDBCol size='12' sm='12' md='7' className='wds-preset'>
                  <MDBBtnGroup>
                    <MDBBtn
                      type='button'
                      disabled={fetching}
                      onClick={() => this._onDateChange(DateManager.getDayInPast('Sun', this.state.reportDate))}
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>
                        <MDBIcon icon='arrow-left' />
                      </div>
                      &nbsp;<div>Previous Week</div>
                    </MDBBtn>
                    <MDBBtn
                      type='button'
                      disabled={fetching}
                      onClick={() => this._onDateChange(new Date())}
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>Current Week</div>
                    </MDBBtn>
                    <MDBBtn
                      type='button'
                      disabled={fetching || !this._canAdvanceDate()}
                      onClick={() =>
                        this._onDateChange(DateManager.getDayInPast('Sun', addWeeks(this.state.reportDate, 2)))
                      }
                      color={this.state.metric !== 'points' ? 'indigo' : 'blue-grey'}
                    >
                      <div>Next Week</div>
                      &nbsp;
                      <div>
                        <MDBIcon icon='arrow-right' />
                      </div>
                    </MDBBtn>
                  </MDBBtnGroup>
                </MDBCol>
                {UserProfileService.isA(
                  [
                    'agency-owner',
                    'system-admin',
                    'internal-admin',
                    'internal-staff',
                    'divisional-leader',
                    'district-leader',
                    'regional-leader',
                    'premier-agent',
                    'division-admin',
                    'region-admin',
                    'district-admin',
                  ],
                  true
                ) && (
                  <MDBCol>
                    <MDBBtn
                      type='button'
                      className='btn-block exportbtn'
                      color='indigo'
                      onClick={this.toggleExportGrowthReportModal}
                    >
                      <MDBIcon icon='file-export' className='mr-1' /> Export Growth Report
                    </MDBBtn>
                  </MDBCol>
                )}
              </MDBRow>
            </MDBContainer>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='current-year'>
              <MDBCardHeader className='default-color' color='default-color'>
                Current Year
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>
                      Week # {getISOWeek(startOfWeek(this.state.reportDate, { weekStartsOn: 1 }))}
                    </h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.wtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Month - {format(this.state.reportDate, 'LLLL')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.mtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Quarter {getQuarter(this.state.reportDate)}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.qtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Year {format(this.state.reportDate, 'yyyy')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.current?.ytd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>{format(this.state.reportDate, 'yyyy')} Per-Day</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                  <MDBCol size='12'>
                    <h5 className='float-left'>{format(this.state.reportDate, 'yyyy')} Forecast</h5>
                    <h5 className='float-right'>
                      {!fetching
                        ? this._formatMetric(
                            this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.forecastedValue
                          )
                        : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='previous-year'>
              <MDBCardHeader color='primary-color' className='prev-year primary-color'>
                Previous Year{' '}
                <span className='ml-3'>
                  {this.state.fetching ? <i className='fa fa-spin fa-spinner'></i> : this._renderPrevYearDropDown()}
                </span>
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>
                      Week # {getISOWeek(startOfWeek(this.state.reportDate, { weekStartsOn: 1 }))}
                    </h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.wtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Month - {format(this.state.reportDate, 'LLLL')}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.mtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Quarter {getQuarter(this.state.reportDate)}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.qtd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='float-left'>Year {this.state.prevYear}</h5>
                    <h5 className='float-right'>
                      {!fetching ? this._formatMetric(this.state.data?.previous?.ytd[this.state.metric] || 0) : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>{this.state.prevYear} Per-Day</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.previous?.ytd[this.state.metric] || 0, 'prev_year')
                                ?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol size='12' sm='12' md='4'>
            <MDBCard className='year-growth'>
              <MDBCardHeader color='secondary-color' className='secondary-color'>
                % Difference
              </MDBCardHeader>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.wtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.mtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.qtd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12'>
                    <h5 className='text--center'>
                      {!fetching ? (this.state.data?.growth?.ytd[this.state.metric] * 100).toFixed(2) + '%' : '-'}
                    </h5>
                  </MDBCol>
                </MDBRow>
                <MDBRow className='card-footer'>
                  {ENABLE_PER_DAY ? (
                    <MDBCol size='12'>
                      <h6 className='float-left'>Per-Day Difference</h6>
                      <h6 className='float-right'>
                        {!fetching
                          ? this._formatMetric(
                              this._forecastMetric(this.state.data?.current?.ytd[this.state.metric] || 0)?.dailyRate -
                                this._forecastMetric(
                                  this.state.data?.previous?.ytd[this.state.metric] || 0,
                                  'prev_year'
                                )?.dailyRate,
                              true
                            )
                          : '-'}
                      </h6>
                    </MDBCol>
                  ) : (
                    <></>
                  )}
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
        <ExportLeaderGrowthReportModal
          isOpen={this.state.isModalOpen}
          toggle={this.toggleExportGrowthReportModal}
          isLoading={this.state.isLoading}
          fetching={fetching}
          exportmetric={this.state.exportmetric}
          setExportMetric={metric => this.setState({ exportmetric: metric })}
          exportData={this.state.exportData}
          exportReportDate={this.state.exportReportDate}
          onExportDateChange={this._onExportDateChange}
          onExportTimespanChange={this._onExportTimespanChange}
          exportTimespanInd={this.state.exportTimespanInd}
          canAdvanceExportDate={this._canAdvanceExportDate}
          buildTableRecords={this._buildTableRecords}
          renderPrevYearDropDown={this._renderExportPrevYearDropDown}
        />
      </MDBContainer>
    )
  }
}

export default LeaderWidget
